import { memo, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { useMediaChannelUpdateMutation } from 'hooks/mutations/publishing'
import {
  mediaChannelConfigRoute,
  mediaChannelEntriesOverviewRoute,
  mediaChannelRoute,
} from 'pages/NWPlatform/MediaChannels/MediaChannel'
import { mediaChannelQueryKeys } from 'services/queryKeys'

const StructureRaw = () => {
  const { t } = useTranslation(['pages', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { mediaChannelId } = mediaChannelRoute.useParams()

  const mediaChannelQuery = useQuery(mediaChannelQueryKeys.detail({ channelId: mediaChannelId }))
  const { data: mediaChannel } = mediaChannelQuery

  const { mutate: updateMediaChannel, ...mediaChannelUpdateMutation } = useMediaChannelUpdateMutation()

  const handleUpdateMediaChannelName = useCallback(
    (title: string, closeCallback: () => void) => {
      if (mediaChannel) {
        updateMediaChannel(
          {
            channelId: mediaChannelId,
            mediaChannelEdit: {
              name: title,
            },
          },
          {
            onError: () => enqueueSnackbar(t('pages:MediaChannel.updateError')),
            onSettled: closeCallback,
          },
        )
      }
    },
    [t, mediaChannel, updateMediaChannel, mediaChannelId, enqueueSnackbar],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={mediaChannelQuery.data?.name ?? ''}
          isLoading={mediaChannelQuery.isLoading}
          isSaving={mediaChannelUpdateMutation.isPending}
          onSave={handleUpdateMediaChannelName}
          TabNavigation={
            <Tabs>
              <NavigationTabLink from={mediaChannelRoute.fullPath} to={mediaChannelConfigRoute.to}>
                {t('pages:MediaChannel.config')}
              </NavigationTabLink>
              <NavigationTabLink from={mediaChannelRoute.fullPath} to={mediaChannelEntriesOverviewRoute.to}>
                {t('entity:publishing.entry', { count: 2 })}
              </NavigationTabLink>
            </Tabs>
          }
        />
      }
    />
  )
}

export const Structure = memo(StructureRaw)
